import { errorToast } from '@hooks/toast.hook';
import { useCallback, useEffect, useRef, useState } from 'react';

const formatJson = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

type UseClipboardCopyReturn = {
  handleCopy: (label: string, payload: unknown) => Promise<void>;
  isCopied: (label: string) => boolean;
};

/**
 * Hook for copying text to clipboard with temporary "copied" state feedback.
 * Handles null/undefined/empty values and shows error toasts.
 */
export const useClipboardCopy = (): UseClipboardCopyReturn => {
  const [copiedStates, setCopiedStates] = useState<Set<string>>(new Set());
  const timeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const handleCopy = useCallback(async (label: string, payload: unknown) => {
    if (payload === null || payload === undefined) {
      errorToast({ description: `No ${label.toLowerCase()} available to copy.` });
      return;
    }

    const text = typeof payload === 'string' ? payload : formatJson(payload);
    const trimmedText = text.trim();

    if (trimmedText.length === 0 || ['{}', '[]'].includes(trimmedText)) {
      errorToast({ description: `No ${label.toLowerCase()} available to copy.` });
      return;
    }

    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);

        // Clear any existing timeout for this label
        const existingTimeout = timeoutsRef.current.get(label);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
        }

        // Set the copied state
        setCopiedStates((prev) => new Set(prev).add(label));

        // Set timeout to reset the copied state after 3 seconds
        const timeout = setTimeout(() => {
          setCopiedStates((prev) => {
            const newSet = new Set(prev);
            newSet.delete(label);
            return newSet;
          });
          timeoutsRef.current.delete(label);
        }, 3000);

        timeoutsRef.current.set(label, timeout);
      } else {
        errorToast({ description: 'Clipboard not supported in this browser.' });
      }
    } catch (error) {
      console.error('Error copying to clipboard', error);
      errorToast({ description: `Unable to copy ${label.toLowerCase()}.` });
    }
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      timeoutsRef.current.clear();
    };
  }, []);

  const isCopied = useCallback((label: string) => copiedStates.has(label), [copiedStates]);

  return { handleCopy, isCopied };
};
